/**
 * GradientButton Component
 *
 * Modern button with gradient effects, animations, and comprehensive variants
 * Extends the base Button component with gradient styling options
 */
import * as React from 'react';
import { motion } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@cleverbooking-fe-monorepo/shared-utils';
import { Loader2 } from 'lucide-react';

const gradientButtonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-95 [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-md hover:shadow-lg focus-visible:ring-blue-500',
        secondary:
          'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-md hover:shadow-lg focus-visible:ring-purple-500',
        success:
          'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-md hover:shadow-lg focus-visible:ring-green-500',
        warning:
          'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg focus-visible:ring-orange-500',
        danger:
          'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-md hover:shadow-lg focus-visible:ring-red-500',
        neutral:
          'bg-gradient-to-r from-gray-500 to-slate-500 hover:from-gray-600 hover:to-slate-600 text-white shadow-md hover:shadow-lg focus-visible:ring-gray-500',
        outline:
          'border-2 border-blue-500 bg-transparent text-blue-600 hover:bg-blue-50 focus-visible:ring-blue-500 dark:text-blue-400 dark:hover:bg-blue-950',
        ghost:
          'text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-400 dark:text-gray-300 dark:hover:bg-gray-800',
      },
      size: {
        sm: 'h-8 px-3 text-xs rounded-md gap-1.5',
        default: 'h-10 px-4 py-2',
        lg: 'h-12 px-6 text-base',
        xl: 'h-14 px-8 text-lg',
        icon: 'size-10',
      },
      rounded: {
        default: 'rounded-lg',
        full: 'rounded-full',
        md: 'rounded-md',
        none: 'rounded-none',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      rounded: 'default',
    },
  }
);

export interface GradientButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof gradientButtonVariants> {
  children?: React.ReactNode;
  isLoading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  animate?: boolean;
}

export const GradientButton = React.forwardRef<HTMLButtonElement, GradientButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      children,
      isLoading = false,
      loadingText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      animate = true,
      disabled,
      ...props
    },
    ref
  ) => {
    const buttonContent = (
      <>
        {isLoading && <Loader2 className="animate-spin" />}
        {!isLoading && leftIcon && leftIcon}
        <span>{isLoading && loadingText ? loadingText : children}</span>
        {!isLoading && rightIcon && rightIcon}
      </>
    );

    const buttonClasses = cn(
      gradientButtonVariants({ variant, size, rounded }),
      fullWidth && 'w-full',
      className
    );

    if (animate) {
      return (
        <motion.button
          ref={ref}
          className={buttonClasses}
          disabled={disabled || isLoading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          {...(props as any)}
        >
          {buttonContent}
        </motion.button>
      );
    }

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={disabled || isLoading}
        {...props}
      >
        {buttonContent}
      </button>
    );
  }
);

GradientButton.displayName = 'GradientButton';

export { gradientButtonVariants };
